import type {AxiosError} from "axios";

import {GEOLOCATION_PERMISSION_DENIED_CODE} from "@/src/modules/core/lib/geolocation-platform";
import {getGeolocationState} from "@/src/modules/core/store/geolocation";

import {captureGlitchtipEvent} from "./capture";
import {sessionCaptureOnce} from "./capture-session";
import {resolveErrorCode, SYNTHETIC_ERROR_CODES} from "./error-codes";
import {
  collectRealtimeClientDiagnostics,
  isGeolocationPermissionDeniedError,
  resolveAxiosCodeTag,
  resolveErrorKind,
  resolveGeolocationErrorCodeTag,
  resolveServiceWorkerActiveTag,
  type GlitchtipDiagnostics,
  type RealtimeConnectTrigger,
  type RealtimeFailurePhase,
} from "./diagnostics";
import {
  DEVICE_FAILURE_ERROR_CODES,
  ERROR_CODE_FAILURE_DOMAIN,
  type DeviceFailureErrorCode,
  type FailureDomain,
} from "./failure-domains";
import {PLATFORM_FEATURE} from "./feature-registry";
import {type HttpContext} from "./http-context";
import {resolveFeatureFromWindowLocation} from "./resolve-feature";
import {shouldCaptureBoundaryError} from "./should-capture-boundary";

export function isGpsCaptureSkipped(): boolean {
  if (typeof window === "undefined") return true;

  const path = window.location.pathname;
  const hashRoute = window.location.hash.replace(/^#/, "");
  return path === "/location-denied" || hashRoute === "/location-denied";
}

export function mapGeolocationErrorCode(
  error: GeolocationPositionError | null | undefined,
): DeviceFailureErrorCode {
  if (!error) return DEVICE_FAILURE_ERROR_CODES.GPS_UNAVAILABLE;

  switch (error.code) {
    case GEOLOCATION_PERMISSION_DENIED_CODE:
      return DEVICE_FAILURE_ERROR_CODES.GPS_PERMISSION_DENIED;
    case 2:
      return DEVICE_FAILURE_ERROR_CODES.GPS_UNAVAILABLE;
    case 3:
      return DEVICE_FAILURE_ERROR_CODES.GPS_TIMEOUT;
    default:
      return DEVICE_FAILURE_ERROR_CODES.GPS_UNAVAILABLE;
  }
}

type DeviceFailureCaptureInput = {
  feature: string;
  errorCode: DeviceFailureErrorCode | string;
  operation?: string;
  failureDomain?: FailureDomain;
  level?: "warning" | "error";
  axiosError?: AxiosError;
  error?: unknown;
  http?: HttpContext;
  extra?: Record<string, string | number | undefined>;
  diagnostics?: GlitchtipDiagnostics;
  sessionKey?: string;
  skipDedupe?: boolean;
};

export function captureDeviceFailure(input: DeviceFailureCaptureInput): void {
  if (input.sessionKey && !sessionCaptureOnce(input.sessionKey)) return;

  const errorCode = input.errorCode;
  const failureDomain =
    input.failureDomain ??
    (typeof errorCode === "string" && errorCode in ERROR_CODE_FAILURE_DOMAIN
      ? ERROR_CODE_FAILURE_DOMAIN[errorCode as DeviceFailureErrorCode]
      : undefined);

  captureGlitchtipEvent({
    feature: input.feature,
    errorCode,
    operation: input.operation,
    failureDomain,
    errorSource: "domain",
    axiosError: input.axiosError,
    error: input.error,
    level: input.level ?? "warning",
    extra: input.extra,
    diagnostics: input.diagnostics,
    http: input.http,
    useMessageTitle: input.error === undefined,
    skipDedupe: input.skipDedupe,
  });
}

export type GpsCaptureContext = {
  feature: string;
  operation: string;
};

export function captureGpsFailure(context: GpsCaptureContext): void {
  if (isGpsCaptureSkipped()) return;

  const geoError = getGeolocationState().error;
  const errorCode = mapGeolocationErrorCode(geoError);

  captureDeviceFailure({
    feature: context.feature,
    errorCode,
    operation: context.operation,
    level: "warning",
    diagnostics: {
      geo_error_code: resolveGeolocationErrorCodeTag(geoError),
      error_kind: geoError?.message ? resolveErrorKind(geoError) : undefined,
    },
    extra: {
      gps_permission_denied: isGeolocationPermissionDeniedError(geoError) ? 1 : 0,
    },
  });
}

export function captureLocationRequiredFailure(feature?: string): void {
  if (isGpsCaptureSkipped()) return;

  captureDeviceFailure({
    feature: feature ?? resolveFeatureFromWindowLocation(),
    errorCode: DEVICE_FAILURE_ERROR_CODES.LOCATION_REQUIRED,
    level: "warning",
  });
}

export function captureFilePickFailed(feature: string, operation?: string): void {
  captureDeviceFailure({
    feature,
    errorCode: DEVICE_FAILURE_ERROR_CODES.FILE_PICK_FAILED,
    operation,
    level: "warning",
  });
}

export function captureImageCompressFailed(feature: string, operation?: string): void {
  captureDeviceFailure({
    feature,
    errorCode: DEVICE_FAILURE_ERROR_CODES.IMAGE_COMPRESS_FAILED,
    operation,
    level: "warning",
  });
}

export function captureMediaUploadFailed(
  feature: string,
  operation?: string,
  axiosError?: AxiosError,
): void {
  captureDeviceFailure({
    feature,
    errorCode: DEVICE_FAILURE_ERROR_CODES.MEDIA_UPLOAD_FAILED,
    operation,
    axiosError,
    level: "error",
  });
}

export function captureMediaUploadOffline(feature: string, operation?: string): void {
  captureDeviceFailure({
    feature,
    errorCode: DEVICE_FAILURE_ERROR_CODES.MEDIA_UPLOAD_FAILED,
    operation,
    failureDomain: "network",
    level: "warning",
    extra: {offline: 1},
  });
}

export function captureConnectionLost(): void {
  if (!sessionCaptureOnce("network:connection_lost")) return;

  captureDeviceFailure({
    feature: "platform",
    errorCode: DEVICE_FAILURE_ERROR_CODES.CONNECTION_LOST,
    level: "warning",
  });
}

export function captureRealtimeConnectionFailed(input: {
  http?: HttpContext;
  phase: RealtimeFailurePhase;
  error?: unknown;
  axiosError?: AxiosError;
  connectTrigger?: RealtimeConnectTrigger;
  configFetchAttempts?: number;
  configFetchDurationMs?: number;
  reconnectAttempt?: number;
}): void {
  if (!sessionCaptureOnce(`realtime:${input.phase}`)) return;

  const clientDiagnostics = collectRealtimeClientDiagnostics();

  captureDeviceFailure({
    feature: PLATFORM_FEATURE,
    errorCode: DEVICE_FAILURE_ERROR_CODES.REALTIME_CONNECTION_FAILED,
    http: input.http,
    error: input.error,
    axiosError: input.axiosError,
    level: "warning",
    diagnostics: {
      realtime_failure_phase: input.phase,
      realtime_connect_trigger: input.connectTrigger ?? "initial",
      route_feature: resolveFeatureFromWindowLocation(),
      error_kind: resolveErrorKind(input.error ?? input.axiosError),
      axios_code: resolveAxiosCodeTag(input.axiosError),
      config_fetch_attempts: input.configFetchAttempts,
      config_fetch_duration_ms: input.configFetchDurationMs,
      reconnect_attempt: input.reconnectAttempt,
      sw_active: resolveServiceWorkerActiveTag(),
      ...clientDiagnostics,
    },
  });
}

export function captureBroadcastingAuthFailed(http?: HttpContext, error?: unknown): void {
  if (!sessionCaptureOnce("realtime:ws_auth")) return;

  captureDeviceFailure({
    feature: PLATFORM_FEATURE,
    errorCode: DEVICE_FAILURE_ERROR_CODES.BROADCASTING_AUTH_FAILED,
    http,
    error,
    level: "warning",
    diagnostics: {
      realtime_failure_phase: "ws_auth",
      route_feature: resolveFeatureFromWindowLocation(),
      error_kind: resolveErrorKind(error),
    },
  });
}

export function capturePushPermissionDenied(): void {
  if (!sessionCaptureOnce("notification:permission_denied")) return;

  captureDeviceFailure({
    feature: "platform",
    errorCode: DEVICE_FAILURE_ERROR_CODES.PUSH_PERMISSION_DENIED,
    level: "warning",
  });
}

export function capturePushSubscriptionFailed(axiosError?: AxiosError): void {
  if (!sessionCaptureOnce("notification:subscription_failed")) return;

  captureDeviceFailure({
    feature: "platform",
    errorCode: DEVICE_FAILURE_ERROR_CODES.PUSH_SUBSCRIPTION_FAILED,
    axiosError,
    level: "warning",
  });
}

const PLATFORM_SYNTHETIC_ERROR_CODES = new Set<string>(Object.values(SYNTHETIC_ERROR_CODES));

export function captureUnhandledRejection(reason: unknown): void {
  if (!shouldCaptureBoundaryError(reason)) return;

  const errorCode = resolveErrorCode(reason);
  const feature = PLATFORM_SYNTHETIC_ERROR_CODES.has(errorCode)
    ? PLATFORM_FEATURE
    : resolveFeatureFromWindowLocation();

  captureGlitchtipEvent({
    error: reason,
    feature,
    errorCode,
    failureDomain: "crash",
    errorSource: "boundary",
    level: "error",
    diagnostics: {
      error_kind: resolveErrorKind(reason),
      sw_active: resolveServiceWorkerActiveTag(),
    },
    useMessageTitle: true,
  });
}

export {DEVICE_FAILURE_ERROR_CODES};
